import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface LayoutVariationOption {
	value: string;
	image: string;
	labelKey: string;
}

const DEFAULT_LAYOUT_VARIATIONS: LayoutVariationOption[] = [
	{
		value: 'DEFAULT',
		image: 'side-by-side',
		labelKey: 'layoutVariationOptions.sideBySide'
	},
	{
		value: 'HORIZONTAL',
		image: 'horizontal',
		labelKey: 'layoutVariationOptions.horizontal'
	},
	{
		value: 'PANORAMIC',
		image: 'panoramic',
		labelKey: 'layoutVariationOptions.panoramic'
	}
];

@Component({
	selector: 'app-layout-variation-selector',
	templateUrl: './layout-variation-selector.component.html',
	styleUrls: ['./layout-variation-selector.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LayoutVariationSelectorComponent),
			multi: true
		}
	]
})
export class LayoutVariationSelectorComponent implements ControlValueAccessor {
	@Input() labelKey = 'promotedContents.realEstateFilterOptions.layoutVariation';
	@Input() imageBasePath = '/assets/images/promoted-content/layout-offer/';
	@Input() defaultValue = 'DEFAULT';
	@Input() options: LayoutVariationOption[] = DEFAULT_LAYOUT_VARIATIONS;
	@Output() readonly selectionChange = new EventEmitter<string>();

	value = this.defaultValue;
	disabled = false;

	private onChange: (value: string) => void = () => {};
	private onTouched: () => void = () => {};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: string | null | undefined): void {
		this.value = value || this.defaultValue;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	onSelect(value: string): void {
		this.value = value;
		this.onChange(value);
		this.onTouched();
		this.selectionChange.emit(value);
		this.cdr.markForCheck();
	}

	getImageSrc(variation: LayoutVariationOption): string {
		return `${this.imageBasePath}${variation.image}.svg`;
	}

	trackByValue(_: number, variation: LayoutVariationOption): string {
		return variation.value;
	}
}
